import React, { useState, useRef } from 'react';
import _ from 'lodash';
import { Field, ErrorMessage } from 'formik';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { colorStyle } from 'grommet-styles';
import { CircleInformation } from 'grommet-icons';

import { Box, Stack, Paragraph, Text, TextInput } from 'components/Wrapped';

export const PlaceHolder = styled(Box)`
  ${(props) =>
    props.hasFocus &&
    css`
      transform: scale(0.75) translateY(-90%);
      transform-origin: left;
    `} transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const TextField = styled(TextInput)`
  border: none;
  border-radius: 56px;
  color: #B4B6B7;
  font-size: 14px;
  font-weight: 500;
  padding: 11px 12px 11px 0;

  ${(props) =>
    props.hasFocus &&
    css`
      box-shadow: none;
      border-color: unset;
    `}

  ${(props) =>
    props.hasAddonStart &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}

    ${(props) =>
      props.hasAddonEnd &&
      css`
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      `}


  ${(props) => props.hasErrors && colorStyle('border-color', 'askColor', props.theme)}
    
  ${(props) =>
    props.customBgColor &&
    css`
      background-color: ${props.customBgColor};
    `}

  ${(props) =>
    props.customPadding &&
    css`
      padding: ${props.customPadding};
    `}
`;

const Addon = ({ background = 'background-5', content = '', isEnd = false, roundSize = 'xxsmall', ...rest }) => (
  <Box
    tabIndex="-1"
    round={{ corner: isEnd ? 'right' : 'left', size: roundSize }}
    pad={{ horizontal: 'small' }}
    background={background}
    justify="center"
    flex="grow"
    {...rest}
  >
    {typeof content === 'string' ? <Paragraph>{content}</Paragraph> : content}
  </Box>
);

export const StlyedAddon = styled(Addon)`
  border-radius: 56px 0 0 56px;

  ${(props) =>
    props.onClick &&
    css`
      cursor: pointer;
    `};
`;

export const StyledTextInput = ({ addonStart, addonEnd, background, ...rest }) => {
  const [isFocused, setIsFocused] = useState(false);
  const searchInput = useRef(null);

  function focus() {
    // Explicitly focus the text input using the raw DOM API
    // Note: we're accessing "current" to get the DOM node
    searchInput.current.focus();
  }
  return (
    <Box direction="row" pad="none" round={false}>
      {addonStart && <StlyedAddon {...addonStart} />}
      <Box round={false} pad="none" fill={true}>
        <TextField
          ref={searchInput}
          background={background}
          focusIndicator={false}
          hasAddonStart={!_.isEmpty(addonStart)}
          hasAddonEnd={!_.isEmpty(addonEnd)}
          hasFocus={isFocused}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
          }}
          {...rest}
        />
      </Box>
      {addonEnd && <StlyedAddon onClick={focus} {...addonEnd} isEnd={true} />}
    </Box>
  );
};

const WrappedTextInput = ({
  name,
  placeholder,
  type,
  persistentPlaceholder,
  addonStart,
  addonEnd,
  value,
  margin = { bottom: '38px' },
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Box pad="none" margin={margin} round={false}>
      <Field name={name}>
        {({ field, form: { handleBlur, touched, errors } }) => (
          <Box direction="row" pad="none" round={false}>
            {addonStart && <StlyedAddon {...addonStart} />}
            <Box round={false} pad="none" fill={true}>
              <Stack anchor="left" interactiveChild="first">
                <TextField
                  {...field}
                  {...rest}
                  type={type}
                  onFocus={() => setIsFocused(true)}
                  onBlur={(e) => {
                    handleBlur(e);
                    setIsFocused(false || !_.isEmpty(field.value));
                  }}
                  hasErrors={_.get(touched, field.name) && _.get(errors, field.name)}
                  hasFocus={isFocused}
                  focusIndicator={false}
                  hasAddonStart={!_.isEmpty(addonStart)}
                  hasAddonEnd={!_.isEmpty(addonEnd)}
                  placeholder={!persistentPlaceholder && placeholder}
                  value={value}
                />
                {persistentPlaceholder && (
                  <PlaceHolder
                    hasFocus={isFocused || field.value}
                    pad="xsmall"
                    margin={{ left: 'small' }}
                    round={false}
                    background="transparent"
                  >
                    <Text color="#B4B6B7" size="small" weight={500}>
                      {placeholder}
                    </Text>
                  </PlaceHolder>
                )}
              </Stack>
            </Box>
            {addonEnd && <StlyedAddon {...addonEnd} isEnd={true} />}
          </Box>
        )}
      </Field>
      <ErrorMessage
        name={name}
        render={(msg) => (
          <Box pad="none" align="center" direction="row" gap="4px" style={{ position: 'absolute', top: '73px' }}>
            <CircleInformation color="#E56150" size="16px" />
            <Text color="white" size="small">
              {msg}
            </Text>
          </Box>
        )}
      />
    </Box>
  );
};

WrappedTextInput.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string.isRequired,
  persistentPlaceholder: PropTypes.bool,
};

WrappedTextInput.defaultProps = {
  type: 'text',
  persistentPlaceholder: true,
};

export default WrappedTextInput;
